// src/stores/createCollectionStore.ts
//
// A tiny module-level observable store for a collection fetched from the backend.
// One instance per data type (orders, questions). All React components that use
// the corresponding hook share the SAME cache and the SAME in-flight request, so
// mounting N widgets triggers a single fetch — not N. Read-flags persist in
// localStorage so they survive refetches. No IndexedDB: MySQL is the source of truth.

export interface CollectionItem {
  id: string;
  createdAt: string;
  isRead?: boolean;
}

export interface CollectionStore<T extends CollectionItem> {
  /** Subscribe to changes (for useSyncExternalStore). Returns an unsubscribe fn. */
  subscribe: (listener: () => void) => () => void;
  /** Current cached array (stable reference until it changes). */
  getSnapshot: () => T[];
  /** Whether a fetch is currently in flight. */
  getLoading: () => boolean;
  /** Fetch from the backend. Concurrent calls share one request; seedOnce keeps the first load. */
  refresh: () => Promise<void>;
  /** Mark ids as read: persist flag + update cache in place. */
  markRead: (ids: string[]) => void;
  /** Remove ids from the local cache (used after a backend delete, or in mock mode). */
  removeLocal: (ids: string[]) => void;
  /** Prepend a freshly created item to the cache. */
  prepend: (item: T) => void;
}

export function createCollectionStore<T extends CollectionItem>(opts: {
  fetcher: () => Promise<T[]>;
  /** localStorage key holding the set of read ids. */
  readKey: string;
  /** When true (mock/dev mode), the first load is kept — refresh() won't regenerate. */
  seedOnce?: boolean;
  /** If > 0, poll the backend every pollMs while at least one component is mounted
   *  and the tab is visible (real-time-ish updates without a websocket). */
  pollMs?: number;
}): CollectionStore<T> {
  const { fetcher, readKey, seedOnce = false, pollMs = 0 } = opts;

  let cache: T[] = [];
  let loaded = false;
  let loading = false;
  let inflight: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((fn) => fn());

  const getReadIds = (): Set<string> => {
    try {
      return new Set(JSON.parse(localStorage.getItem(readKey) || '[]'));
    } catch {
      return new Set();
    }
  };
  const saveReadIds = (ids: Set<string>) =>
    localStorage.setItem(readKey, JSON.stringify([...ids]));

  const applyReadFlags = (arr: T[]): T[] => {
    const ids = getReadIds();
    if (ids.size === 0) return arr;
    return arr.map((x) => (ids.has(x.id) ? { ...x, isRead: true } : x));
  };

  // silent = true → don't toggle the loading flag (used by background polling,
  // so widgets don't flash a spinner every poll).
  async function refresh(silent = false): Promise<void> {
    // In seedOnce (mock) mode, never clobber the already-loaded in-memory set.
    if (loaded && seedOnce) {
      emit();
      return;
    }
    if (inflight) return inflight;

    if (!silent) {
      loading = true;
      emit();
    }
    inflight = (async () => {
      try {
        const data = await fetcher();
        cache = applyReadFlags(data);
        loaded = true;
      } catch (e) {
        // Keep the previous cache; surface the error for diagnostics.
        // eslint-disable-next-line no-console
        console.error(`[collectionStore:${readKey}] fetch failed:`, e);
      } finally {
        loading = false;
        inflight = null;
        emit();
      }
    })();
    return inflight;
  }

  function markRead(ids: string[]): void {
    if (!ids.length) return;
    const set = getReadIds();
    ids.forEach((id) => set.add(id));
    saveReadIds(set);
    const idSet = new Set(ids);
    cache = cache.map((x) => (idSet.has(x.id) && !x.isRead ? { ...x, isRead: true } : x));
    emit();
  }

  function removeLocal(ids: string[]): void {
    if (!ids.length) return;
    const set = getReadIds();
    ids.forEach((id) => set.delete(id));
    saveReadIds(set);
    const idSet = new Set(ids);
    cache = cache.filter((x) => !idSet.has(x.id));
    emit();
  }

  function prepend(item: T): void {
    cache = [applyReadFlags([item])[0], ...cache];
    emit();
  }

  // ── background polling (only while observed + tab visible) ──────────────────
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  const isVisible = () =>
    typeof document === 'undefined' || document.visibilityState === 'visible';

  const pollTick = () => { if (isVisible()) void refresh(true); };
  const onVisibility = () => { if (isVisible()) void refresh(true); };

  const startPolling = () => {
    if (pollTimer || !pollMs || seedOnce) return;
    pollTimer = setInterval(pollTick, pollMs);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
  };
  const stopPolling = () => {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  };

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      if (listeners.size === 1) startPolling();
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) stopPolling();
      };
    },
    getSnapshot: () => cache,
    getLoading: () => loading,
    refresh,
    markRead,
    removeLocal,
    prepend,
  };
}
